import type { Command } from "../document/commands.ts";
import { ownFootprintDrops } from "../document/footprint-cells.ts";
import type { JsonValue, MapDocument, PropBehaviour, PropInstance, TileRef } from "../document/types.ts";
import { changePropsCommand, keepPropsPlacementCommand, together, mirrorTarget, movePropsCommand, movePropsToLayerCommand, propsBehaviourCommand, propsCarrying, propsPlacing, removePropsCommand, selectedProps, sharedFields, sharedFrame, sharedType } from "./many-props.ts";
import type { ObjectTypeDefinition } from "./objects.ts";
import { changePropCommand } from "./panel-commands.ts";
import { planSortInFront, sortInFrontCounts, type SortFrameOf, type SortInFrontCounts, type SortInFrontEntry } from "./sort-in-front.ts";
import type { PropRef } from "./templates.ts";

/**
 * The prop selection's edits (P1.6-T33, ADR-070's amendment "many props at once"): each acts on every selected
 * prop as one command that one undo takes back, and refuses whole, recording nothing, when a lock or a hidden
 * layer refuses any part of it, as every command does. The behaviour clipboard Copy behaviour fills is view
 * state here, outside history and never saved; filling it tells the store's subscribers, so Paste behaviour
 * wakes at once. Sort just in front of what is behind (ADR-040's amendment of 2026-10-06, decision 12) reads
 * each frame's base, art bounds and ground mark, which only the host's asset manifest holds, through the lookup
 * the host binds (`bindSortFrames`).
 */

/**
 * What the edits need from the store.
 */
export interface ManyPropHost {
  readonly map: () => MapDocument;
  readonly run: (command: Command) => boolean;
  readonly selection: () => readonly PropRef[];
  readonly select: (refs: readonly PropRef[]) => void;
  readonly editable: (layerId: string) => boolean;
  readonly locked: (layerId: string) => boolean;
  readonly hidden: (layerId: string) => boolean;
  readonly tileSize: () => number;
  readonly quantise: (value: number) => number;
  readonly stepped: (prop: PropInstance, stepX: number, stepY: number) => { readonly x: number; readonly y: number };
  readonly types: () => readonly ObjectTypeDefinition[];
  readonly switchedOn: (type: ObjectTypeDefinition, prop: PropInstance) => PropBehaviour;
  readonly notify: () => void;
  readonly guardDelete: (refs: readonly PropRef[], run: () => boolean) => boolean;
}

/**
 * What the selected props share, for the inspector: the frame, the behaviour type, the fields of that type
 * with the mixed ones named, the mirrors and the sort offset, each null where the props differ.
 */
export interface SharedProps {
  readonly count: number;
  readonly frame: TileRef | null;
  readonly type: string | null;
  readonly fields: { readonly values: { readonly [key: string]: JsonValue }; readonly mixed: readonly string[] };
  readonly flipX: boolean | null;
  readonly flipY: boolean | null;
  readonly sortPixels: number | null;
}

/**
 * The one value every item gives, or null when they differ or there are none.
 *
 * @param items The items.
 * @param read Reads an item's value.
 * @returns The value, or null.
 */
function same<T, V extends boolean | number>(items: readonly T[], read: (item: T) => V): V | null {
  const first = items[0];
  if (first === undefined) {
    return null;
  }
  const value = read(first);
  return items.every((item) => read(item) === value) ? value : null;
}

/**
 * Edits on the prop selection.
 */
export class ManyPropEdits {
  private clipboard: PropBehaviour | null = null;
  private sortFrames: SortFrameOf | null = null;

  /**
   * @param host The store's side.
   */
  public constructor(private readonly host: ManyPropHost) {}

  /**
   * The selected props the map holds, in the selection's order.
   *
   * @returns The props.
   */
  public props(): readonly PropInstance[] {
    return selectedProps(this.host.map(), this.host.selection()).map((entry) => entry.prop);
  }

  /**
   * What the selected props share.
   *
   * @returns The shared values.
   */
  public shared(): SharedProps {
    const props = this.props();
    const type = sharedType(props);
    return {
      count: props.length,
      frame: sharedFrame(props),
      type,
      fields: type === null ? { values: {}, mixed: [] } : sharedFields(props.map((prop) => prop.behaviour?.fields ?? {})),
      flipX: same(props, (prop) => prop.flipX),
      flipY: same(props, (prop) => prop.flipY),
      sortPixels: same(props, (prop) => prop.sortPixels),
    };
  }

  /**
   * Runs a command built over the selection.
   *
   * @param command The command, or null for nothing to do.
   * @returns False when there was nothing to do or a lock or a hidden layer refused it.
   */
  private runIf(command: Command | null): boolean {
    return command !== null && this.host.run(command);
  }

  /**
   * Runs a command that gives props behaviours and counts the props it left without their own footprint.
   *
   * @param command The command, or null for nothing to do.
   * @returns The count, 0 when nothing ran.
   */
  private runDrops(command: Command | null): number {
    return this.runIf(command) && command !== null ? ownFootprintDrops(command) : 0;
  }

  /**
   * Removes every selected prop as one command, or holds it for its question while a prop it keeps references
   * one of them (P1.6-T35).
   *
   * @returns False when nothing was removed; true once removed or held.
   */
  public remove(): boolean {
    const command = removePropsCommand(this.host.map(), this.host.selection());
    return command !== null && this.host.guardDelete(this.host.selection(), () => this.host.run(command));
  }

  /**
   * Moves every selected prop by whole pixels as one command, as a drag of the selection drops it or
   * Shift+arrows nudge it.
   *
   * @param dx Pixels right.
   * @param dy Pixels down.
   * @returns False when nothing moved.
   */
  public move(dx: number, dy: number): boolean {
    const tileSize = this.host.tileSize();
    const { quantise } = this.host;
    return this.runIf(movePropsCommand(this.host.map(), this.host.selection(), (prop) => ({ x: quantise(prop.x + dx / tileSize), y: quantise(prop.y + dy / tileSize) })));
  }

  /**
   * Moves every selected prop by collision cells as one command, each to its own next cell edge that way
   * (`cellStepped`), so props on the cells move together and stay on them.
   *
   * @param stepX Cells right; negative steps left.
   * @param stepY Cells down; negative steps up.
   * @returns False when nothing moved.
   */
  public step(stepX: number, stepY: number): boolean {
    return this.runIf(movePropsCommand(this.host.map(), this.host.selection(), (prop) => this.host.stepped(prop, stepX, stepY)));
  }

  /**
   * Mirrors every selected prop on one axis as one command: all on, unless every one is mirrored already.
   *
   * @param axis The axis.
   * @returns False when nothing changed.
   */
  public mirror(axis: "x" | "y"): boolean {
    const on = mirrorTarget(this.props(), axis);
    return this.runIf(changePropsCommand(this.host.map(), this.host.selection(), () => (axis === "x" ? { flipX: on } : { flipY: on })));
  }

  /**
   * Sets one axis's mirror on every selected prop as one command, the Placement section's None or Mirrored.
   *
   * @param axis The axis.
   * @param on True to mirror them, false to unmirror them.
   * @returns False when every prop already held it.
   */
  public setMirror(axis: "x" | "y", on: boolean): boolean {
    return this.runIf(changePropsCommand(this.host.map(), this.host.selection(), () => (axis === "x" ? { flipX: on } : { flipY: on })));
  }

  /**
   * Gives every selected prop one sort offset as one command, the value typed into the Placement section.
   *
   * @param value The offset, in whole pixels.
   * @returns False when every prop already held it.
   */
  public setOffset(value: number): boolean {
    return this.runIf(changePropsCommand(this.host.map(), this.host.selection(), () => ({ sortPixels: value })));
  }

  /**
   * Keeps props as placed, or stops keeping them, as one command that one undo takes back (ADR-040 amendment
   * of 2026-10-04, P1.6-T96): none moves, and one already so is left out.
   *
   * @param keep True to keep them as placed, false to stop.
   * @param refs The props to act on; every selected prop when omitted.
   * @returns False when every prop is already so, or a lock or a hidden layer refused it.
   */
  public keepPlacement(keep: boolean, refs: readonly PropRef[] = this.host.selection()): boolean {
    return this.runIf(keepPropsPlacementCommand(this.host.map(), refs, keep));
  }

  /**
   * Why the selected props cannot move to a layer, or null when they can: a locked or hidden target refuses
   * the move whole, as it refuses every edit there.
   *
   * @param layerId The target layer.
   * @returns The reason, or null.
   */
  public moveRefusal(layerId: string): string | null {
    const layer = this.host.map().layers.find((l) => l.id === layerId);
    const name = layer?.name ?? layerId;
    if (this.host.locked(layerId)) {
      return `${name} is locked, so nothing moves; unlock it in Layers first`;
    }
    return this.host.hidden(layerId) ? `${name} is hidden, so nothing moves; show it in Layers first` : null;
  }

  /**
   * Moves every selected prop not already there to another prop layer as one command, each keeping its id,
   * place and state; the selection follows them, since the store finds a selected prop by its id wherever it
   * stands.
   *
   * @param layerId The target layer.
   * @returns False when none moved or a lock or a hidden layer refused the move.
   * @throws Error when the target is not a prop layer.
   */
  public moveToLayer(layerId: string): boolean {
    return this.runIf(movePropsToLayerCommand(this.host.map(), this.host.selection(), layerId));
  }

  /**
   * Shifts every selected prop's sort offset as one command.
   *
   * @param delta Whole pixels to add to each.
   * @returns False when nothing changed.
   */
  public offset(delta: number): boolean {
    return this.runIf(changePropsCommand(this.host.map(), this.host.selection(), (prop) => ({ sortPixels: prop.sortPixels + delta })));
  }

  /**
   * Gives the edits the frames' base, art bounds and ground marks, which only the host's asset manifest holds;
   * Sort just in front of what is behind has no plan until then.
   *
   * @param frameOf The lookup, read at each plan, or null to take it away.
   */
  public bindSortFrames(frameOf: SortFrameOf | null): void {
    this.sortFrames = frameOf;
  }

  /**
   * Whether the host has bound the frames Sort just in front of what is behind measures by.
   *
   * @returns True once bound.
   */
  public get sortFramesBound(): boolean {
    return this.sortFrames !== null;
  }

  /**
   * What Sort just in front of what is behind would do to each of some props: its own offset against the props
   * of the band's layers that are not among them, or why it gets none (`planSortInFront`). It reads every prop
   * of the band's layers, so a caller asks when the answer is shown or run, not on every key press.
   *
   * @param refs The props; the prop selection when left out. An inspector showing one prop the selection does
   *   not hold names it here.
   * @returns One entry per prop the map holds and the lookup sizes, or null while the host has bound no frames.
   */
  public sortInFrontPlan(refs: readonly PropRef[] = this.host.selection()): readonly SortInFrontEntry[] | null {
    return this.sortFrames === null ? null : planSortInFront(this.host.map(), refs, this.sortFrames, this.host.tileSize());
  }

  /**
   * Sorts each of some props just in front of what is behind it, as one command of sort offsets: each prop its
   * own offset, worked out from one reading of the map before anything is written and written to the prop the
   * plan names by layer and id; the props with nothing to write are left out.
   *
   * @param refs The props; the prop selection when left out.
   * @returns What was done, by count, or null when nothing changed: no frames are bound, no prop gets an
   *   offset, or a lock, a hidden layer or the session refused the edit.
   */
  public sortInFront(refs: readonly PropRef[] = this.host.selection()): SortInFrontCounts | null {
    if (this.sortFrames === null) {
      return null;
    }
    const map = this.host.map();
    const plan = planSortInFront(map, refs, this.sortFrames, this.host.tileSize());
    const command = together(plan.flatMap((entry) => ("sortPixels" in entry.result ? [changePropCommand(map, entry.layerId, entry.prop.id, { sortPixels: entry.result.sortPixels })] : [])));
    return this.runIf(command) ? sortInFrontCounts(plan) : null;
  }

  /**
   * Switches a behaviour type on for every selected prop not carrying it yet, each as a single prop's switch
   * would (the type's field defaults, the game's suggestion for its own frame's further states, its painted
   * cells kept), or with null takes the behaviour off them all, as one command.
   *
   * @param type The type's id, or null for None.
   * @returns How many props the switch left without the footprint of their own they had, which a prop with a
   *   behaviour cannot keep (`ownFootprintDrops`); 0 when none did or nothing changed.
   * @throws Error when the type is not registered.
   */
  public switchBehaviour(type: string | null): number {
    if (type === null) {
      return this.runDrops(propsBehaviourCommand(this.host.map(), this.host.selection(), () => undefined));
    }
    const definition = this.host.types().find((t) => t.type === type);
    if (definition === undefined) {
      throw new Error(`object type ${type} is not registered`);
    }
    return this.runDrops(propsBehaviourCommand(this.host.map(), this.host.selection(), (prop) => (prop.behaviour?.type === type ? prop.behaviour : this.host.switchedOn(definition, prop))));
  }

  /**
   * Sets one field on every selected prop carrying a behaviour, leaving its other fields as they are, as one
   * command; null clears the field.
   *
   * @param name The field's name.
   * @param value The value, or null to clear it.
   * @returns False when nothing changed.
   */
  public setField(name: string, value: JsonValue | null): boolean {
    return this.runIf(
      propsBehaviourCommand(this.host.map(), this.host.selection(), (prop) => {
        const behaviour = prop.behaviour;
        if (behaviour === undefined) {
          return undefined;
        }
        const { [name]: _dropped, ...rest } = behaviour.fields;
        return { ...behaviour, fields: value === null ? rest : { ...behaviour.fields, [name]: value } };
      }),
    );
  }

  /**
   * The behaviour Copy behaviour last took, or null.
   *
   * @returns The behaviour.
   */
  public get behaviourClipboard(): PropBehaviour | null {
    return this.clipboard;
  }

  /**
   * Why Copy behaviour cannot run, or null when it can: it takes a lone selected prop's behaviour of a type
   * the game registers.
   *
   * @returns The reason, or null.
   */
  public copyRefusal(): string | null {
    const props = this.props();
    const only = props[0];
    if (props.length !== 1 || only === undefined) {
      return "Select one prop carrying a behaviour to copy it";
    }
    if (only.behaviour === undefined) {
      return "The selected prop carries no behaviour";
    }
    const type = only.behaviour.type;
    return this.host.types().some((t) => t.type === type) ? null : `This game does not register ${type}, so it is not copied`;
  }

  /**
   * Copies the lone selected prop's behaviour whole (type, fields, state frames, animations, activation cells)
   * into the clipboard.
   *
   * @returns False when `copyRefusal` refuses.
   */
  public copyBehaviour(): boolean {
    if (this.copyRefusal() !== null) {
      return false;
    }
    this.clipboard = this.props()[0]?.behaviour ?? null;
    this.host.notify();
    return this.clipboard !== null;
  }

  /**
   * Why Paste behaviour cannot run, or null when it can.
   *
   * @returns The reason, or null.
   */
  public pasteRefusal(): string | null {
    if (this.clipboard === null) {
      return "Nothing to paste; copy a prop's behaviour first";
    }
    return this.props().length === 0 ? "Select the props to paste the behaviour onto" : null;
  }

  /**
   * Gives every selected prop a behaviour equal to the clipboard's, as one command.
   *
   * @returns How many props the paste left without the footprint of their own they had (`ownFootprintDrops`);
   *   0 when none did, `pasteRefusal` refuses, every prop already had the behaviour, or a lock refused the
   *   command.
   */
  public pasteBehaviour(): number {
    const behaviour = this.clipboard;
    if (behaviour === null || this.pasteRefusal() !== null) {
      return 0;
    }
    return this.runDrops(propsBehaviourCommand(this.host.map(), this.host.selection(), () => behaviour));
  }

  /**
   * Selects every prop placing the frame the selected props share, on every editable prop layer.
   *
   * @returns False, changing nothing, when the selected props do not share one frame.
   */
  public selectPlacing(): boolean {
    const frame = sharedFrame(this.props());
    if (frame === null) {
      return false;
    }
    this.host.select(propsPlacing(this.host.map(), this.host.editable, frame));
    return true;
  }

  /**
   * Selects every prop carrying the behaviour type the selected props share, on every editable prop layer.
   *
   * @returns False, changing nothing, when the selected props do not share one type.
   */
  public selectCarrying(): boolean {
    const type = sharedType(this.props());
    if (type === null) {
      return false;
    }
    this.host.select(propsCarrying(this.host.map(), this.host.editable, type));
    return true;
  }
}
